import type { ReportingMonetizationReadinessResponse } from '@cast/types';

import type { OnboardingTask } from './types';
import {
  ONBOARDING_TASK_DEFINITIONS,
  type OnboardingTaskDefinition,
} from './taskDefinitions';
import { OnboardingTaskId } from './taskIds';

export type OnboardingWizardVisibilityInput = {
  isMobile: boolean;
  hideForever: boolean;
  isLoading: boolean;
  /** Recommended tasks that are still incomplete. */
  remainingRecommendedCount: number;
  /** When true, hide on the dedicated onboarding wizard route. */
  isOnboardingRoute: boolean;
  /**
   * When false, hide unless forceVisible (Cast Radar / reporting monetization
   * does not apply to this org).
   */
  monetizationApplies: boolean;
  /** Dev Toolbox override: true force show, false force hide, undefined = normal. */
  forceVisible?: boolean;
};

export const shouldShowOnboardingWizardButton = ({
  isMobile,
  hideForever,
  isLoading,
  remainingRecommendedCount,
  isOnboardingRoute,
  monetizationApplies,
  forceVisible,
}: OnboardingWizardVisibilityInput): boolean => {
  if (isMobile || isOnboardingRoute) {
    return false;
  }
  if (forceVisible === false) {
    return false;
  }
  if (forceVisible === true) {
    return true;
  }
  return (
    monetizationApplies &&
    !hideForever &&
    !isLoading &&
    remainingRecommendedCount > 0
  );
};

const getRecommendedTasks = (tasks: OnboardingTask[]): OnboardingTask[] =>
  tasks.filter((task) => task.recommended);

export const getRemainingRecommendedCount = (tasks: OnboardingTask[]): number =>
  getRecommendedTasks(tasks).filter((task) => !task.checked).length;

export const getOnboardingCompletionPercent = (
  tasks: OnboardingTask[]
): number => {
  const recommended = getRecommendedTasks(tasks);
  if (recommended.length === 0) {
    return 0;
  }
  const completed = recommended.filter((task) => task.checked).length;
  return Math.round((completed / recommended.length) * 100);
};

export const getOnboardingProgressLabel = (percent: number): string => {
  if (percent < 0) {
    return '';
  }
  return `${Math.min(100, Math.round(percent))}%`;
};

/** First incomplete task in list order — used to highlight the next action. */
export const getNextIncompleteTaskId = (
  tasks: OnboardingTask[]
): string | undefined => tasks.find((task) => !task.checked)?.id;

export type ResolveTaskCheckedOptions = {
  /**
   * Same-browser OpsPilot chat history fallback while PostHog readiness lags
   * (~15 minutes after the first question).
   */
  hasAskedOpsPilotQuestionLocally?: boolean;
};

export const resolveTaskChecked = (
  definition: OnboardingTaskDefinition,
  readiness: ReportingMonetizationReadinessResponse | undefined,
  { hasAskedOpsPilotQuestionLocally = false }: ResolveTaskCheckedOptions = {}
): boolean => {
  const fromApi = Boolean(readiness?.[definition.readinessFlag]);
  if (definition.id !== OnboardingTaskId.AskOpsPilot) {
    return fromApi;
  }
  return fromApi || hasAskedOpsPilotQuestionLocally;
};

export type BuildOnboardingTasksOptions = {
  definitions?: readonly OnboardingTaskDefinition[];
  /** When false, Ask OpsPilot is omitted and progress excludes it. Defaults to false. */
  isOpsPilotEnabled?: boolean;
  /** localStorage OpsPilot history for this org — ORs with API readiness. */
  hasAskedOpsPilotQuestionLocally?: boolean;
};

export const buildOnboardingTasks = (
  readiness: ReportingMonetizationReadinessResponse | undefined,
  {
    definitions = ONBOARDING_TASK_DEFINITIONS,
    isOpsPilotEnabled = false,
    hasAskedOpsPilotQuestionLocally = false,
  }: BuildOnboardingTasksOptions = {}
): OnboardingTask[] =>
  definitions
    .filter(
      (definition) =>
        definition.id !== OnboardingTaskId.AskOpsPilot || isOpsPilotEnabled
    )
    .map((definition) => ({
      id: definition.id,
      label: definition.label,
      href: definition.getHref(),
      recommended: definition.recommended,
      checked: resolveTaskChecked(definition, readiness, {
        hasAskedOpsPilotQuestionLocally,
      }),
    }));
